import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import { Button, Popup } from 'devextreme-react';
import { LoadPanel } from 'devextreme-react/load-panel';
import axios from 'axios';
import notify from 'devextreme/ui/notify';

const SignatureCapture = ({ visible, onClose, transactionHeaderRowId, accessToken, onSuccess }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const signaturePadRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (visible && canvasRef.current && containerRef.current) {
            // Resize canvas to match container first
            const resizeCanvas = () => {
                const canvas = canvasRef.current;
                const container = containerRef.current;
                if (canvas && container) {
                    // Get container dimensions to ensure canvas fills entire space
                    const containerRect = container.getBoundingClientRect();
                    const width = containerRect.width;
                    const height = containerRect.height;
                    
                    // Only resize if we have valid dimensions
                    if (width > 0 && height > 0) {
                        // Set canvas internal size to match container size exactly
                        // This ensures pointer coordinates match drawing coordinates
                        canvas.width = width;
                        canvas.height = height;
                        
                        // Ensure display size exactly matches container (no percentage, explicit pixels)
                        canvas.style.width = width + 'px';
                        canvas.style.height = height + 'px';
                        canvas.style.position = 'absolute';
                        canvas.style.top = '0';
                        canvas.style.left = '0';
                        
                        // Verify sizes match (for debugging)
                        if (canvas.width !== width || canvas.height !== height) {
                            console.warn('Canvas size mismatch detected. Container:', width, 'x', height, 'Canvas:', canvas.width, 'x', canvas.height);
                        }
                    }
                }
            };

            // Initial resize with delay to ensure container is rendered
            const initialResizeTimer = setTimeout(() => {
                resizeCanvas();
            }, 50);

            // Initialize SignaturePad after canvas is properly sized
            // Use a small delay to ensure canvas dimensions are set
            const initSignaturePad = () => {
                // Ensure canvas is resized before initializing SignaturePad
                resizeCanvas();
                
                if (canvasRef.current && !signaturePadRef.current) {
                    // Verify canvas and container sizes match before initializing
                    const canvas = canvasRef.current;
                    const container = containerRef.current;
                    if (canvas && container) {
                        const containerRect = container.getBoundingClientRect();
                        const containerWidth = containerRect.width;
                        const containerHeight = containerRect.height;
                        
                        if (canvas.width === containerWidth && canvas.height === containerHeight) {
                            signaturePadRef.current = new SignaturePad(canvasRef.current, {
                                backgroundColor: 'rgb(255, 255, 255)',
                                penColor: 'rgb(0, 0, 0)',
                                minWidth: 1,
                                maxWidth: 3,
                                throttle: 16,
                                minDistance: 5
                            });
                        } else {
                            // Retry if sizes don't match
                            console.warn('Canvas and container sizes do not match, retrying...');
                            setTimeout(initSignaturePad, 50);
                        }
                    }
                } else if (signaturePadRef.current) {
                    // If SignaturePad already exists, save data, clear, resize, and restore
                    const data = signaturePadRef.current.toData();
                    signaturePadRef.current.clear();
                    resizeCanvas();
                    // Reinitialize SignaturePad to ensure it uses new canvas dimensions
                    signaturePadRef.current.off();
                    signaturePadRef.current = new SignaturePad(canvasRef.current, {
                        backgroundColor: 'rgb(255, 255, 255)',
                        penColor: 'rgb(0, 0, 0)',
                        minWidth: 1,
                        maxWidth: 3,
                        throttle: 16,
                        minDistance: 5
                    });
                    signaturePadRef.current.fromData(data);
                }
            };

            // Initialize after a brief delay to ensure canvas is ready
            const initTimer = setTimeout(initSignaturePad, 100);

            // Use ResizeObserver to handle container size changes
            let resizeObserver = null;
            if (containerRef.current && window.ResizeObserver) {
                resizeObserver = new ResizeObserver(() => {
                    if (signaturePadRef.current) {
                        const data = signaturePadRef.current.isEmpty() ? null : signaturePadRef.current.toData();
                        // Reinitialize SignaturePad to ensure it uses new canvas dimensions
                        signaturePadRef.current.off();
                        resizeCanvas();
                        signaturePadRef.current = new SignaturePad(canvasRef.current, {
                            backgroundColor: 'rgb(255, 255, 255)',
                            penColor: 'rgb(0, 0, 0)',
                            minWidth: 1,
                            maxWidth: 3,
                            throttle: 16,
                            minDistance: 5
                        });
                        if (data) {
                            signaturePadRef.current.fromData(data);
                        }
                    } else {
                        resizeCanvas();
                    }
                });
                resizeObserver.observe(containerRef.current);
            }

            // Handle window resize as fallback
            const handleResize = () => {
                if (signaturePadRef.current) {
                    const data = signaturePadRef.current.isEmpty() ? null : signaturePadRef.current.toData();
                    // Reinitialize SignaturePad to ensure it uses new canvas dimensions
                    signaturePadRef.current.off();
                    resizeCanvas();
                    signaturePadRef.current = new SignaturePad(canvasRef.current, {
                        backgroundColor: 'rgb(255, 255, 255)',
                        penColor: 'rgb(0, 0, 0)',
                        minWidth: 1,
                        maxWidth: 3,
                        throttle: 16,
                        minDistance: 5
                    });
                    if (data) {
                        signaturePadRef.current.fromData(data);
                    }
                } else {
                    resizeCanvas();
                }
            };

            window.addEventListener('resize', handleResize);

            return () => {
                clearTimeout(initialResizeTimer);
                clearTimeout(initTimer);
                window.removeEventListener('resize', handleResize);
                if (resizeObserver) {
                    resizeObserver.disconnect();
                }
                if (signaturePadRef.current) {
                    signaturePadRef.current.clear();
                    signaturePadRef.current.off();
                    signaturePadRef.current = null;
                }
            };
        }
    }, [visible]);

    const handleClear = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    const handleSave = async () => {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
            notify({
                message: 'Please provide a signature before saving.',
                width: 300,
            }, 'warning', 3000);
            return;
        }

        setIsSaving(true);
        try {
            const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
            
            const apiUrl = `${window._env_.REACT_APP_APIS}/wm/TransactionHeaderOperations/InsertSignature`;
            
            const response = await axios.post(
                apiUrl,
                {
                    transactionHeaderRowId: transactionHeaderRowId,
                    signature: signatureDataURL
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            if (response.data && response.data.success) {
                notify({
                    message: response.data.message || 'Signature saved successfully.',
                    width: 300,
                }, 'success', 3000);
                
                if (onSuccess) {
                    onSuccess();
                }
                handleClose();
            } else {
                notify({
                    message: response.data?.message || 'Error saving signature. Please try again.',
                    width: 300,
                }, 'error', 3000);
            }
        } catch (error) {
            console.error('Error saving signature:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.message || 'Error saving signature. Please try again.';
            notify({
                message: errorMessage,
                width: 300,
            }, 'error', 3000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
        onClose();
    };

    return (
        <>
            <Popup
                visible={visible}
                onHiding={handleClose}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title="Capture Signature"
                closeOnOutsideClick={true}
                width="600px"
                height="420px"
            >
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                    <LoadPanel
                        shadingColor="rgba(0,0,0,0.4)"
                        position={{ of: '#signatureCaptureContainer' }}
                        visible={isSaving}
                        showIndicator={true}
                        shading={true}
                        showPane={true}
                        hideOnOutsideClick={false}
                        message="Saving signature..."
                    />
                    <div id="signatureCaptureContainer" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div 
                            ref={containerRef}
                            style={{ 
                                border: '0px none ', 
                                backgroundColor: '#fff',
                               // marginBottom: '15px',
                                flex: 1,
                                position: 'relative',
                                overflow: 'hidden',
                                padding: 0
                            }}>
                            <canvas
                                ref={canvasRef}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    touchAction: 'none',
                                    cursor: 'crosshair',
                                    display: 'block',
                                    border: '1px solid #ccc',
                                    
                                }}
                            />
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            gap: '10px', 
                            justifyContent: 'flex-end',
                            paddingTop: '10px',
                            borderTop: '1px solid #eee'
                        }}>
                            <Button
                                text="Clear"
                                onClick={handleClear}
                                disabled={isSaving}
                            />
                            <Button
                                text="Save"
                                type="default"
                                onClick={handleSave}
                                disabled={isSaving}
                            />
                            <Button
                                text="Cancel"
                                onClick={handleClose}
                                disabled={isSaving}
                            />
                        </div>
                    </div>
                </div>
            </Popup>
        </>
    );
};

export default SignatureCapture;
